// École Nid Douillet - Contact Page (Dynamic CMS Content)

import React, { useState, useEffect } from 'react';
import { CMSContent } from '../../services/cms.api';
import { cmsApi } from '../../services/cms.api';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';

const ContactPage: React.FC = () => {
  const [content, setContent] = useState<CMSContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContactContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load contact page content from CMS
        const response = await cmsApi.getContentBySlug('contact', 'fr');
        if (response.data) {
          setContent(response.data);
        }
      } catch (err) {
        console.error('Error loading contact content:', err);
        setError('Erreur lors du chargement du contenu. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    loadContactContent();
  }, []);

  // Update page title and meta description
  useEffect(() => {
    if (content) {
      document.title = content.meta_title || content.title || 'Contact - École Nid Douillet';
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && content.meta_description) {
        metaDescription.setAttribute('content', content.meta_description);
      }
    }
  }, [content]);

  if (loading) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Chargement du contenu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Erreur</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-neutral-900 mb-6">
            Contactez-nous
          </h1>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
            <p className="text-yellow-800">Contenu non disponible pour le moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-16">
      {/* Page Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-neutral-900 mb-6">
          {content.title}
        </h1>
        {content.excerpt && (
          <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
            {content.excerpt}
          </p>
        )}
      </div>
      
      <div className="grid lg:grid-cols-2 gap-12">
        {/* Contact Information Cards */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center mb-4">
              <MapPinIcon className="h-6 w-6 text-primary-600 mr-3" />
              <h3 className="text-lg font-semibold text-neutral-900">Adresse</h3>
            </div>
            <p className="text-neutral-600">
              Tilila, Agadir<br />
              Maroc
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center mb-4">
              <PhoneIcon className="h-6 w-6 text-primary-600 mr-3" />
              <h3 className="text-lg font-semibold text-neutral-900">Téléphone</h3>
            </div>
            <p className="text-neutral-600">
              <a href="tel:+212668786368" className="hover:text-primary-600 transition-colors">
                +212 668 78 63 68
              </a>
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center mb-4">
              <EnvelopeIcon className="h-6 w-6 text-primary-600 mr-3" />
              <h3 className="text-lg font-semibold text-neutral-900">Email</h3>
            </div>
            <p className="text-neutral-600">
              <a href="mailto:contact@ecoleniddouillet.com" className="hover:text-primary-600 transition-colors">
                contact@ecoleniddouillet.com
              </a>
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center mb-4">
              <ClockIcon className="h-6 w-6 text-primary-600 mr-3" />
              <h3 className="text-lg font-semibold text-neutral-900">Horaires</h3>
            </div>
            <div className="text-neutral-600 space-y-1">
              <p>Lundi - Vendredi : 8h00 - 17h00</p>
              <p>Samedi : 8h00 - 12h00</p>
              <p className="text-sm text-neutral-500">Fermé le dimanche</p>
            </div>
          </div>
        </div>
        
        {/* Dynamic Content */}
        <div className="bg-white rounded-xl shadow-soft p-8">
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: content.content }}
          />
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="mt-12 text-center">
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-neutral-900 mb-4">
            Prêt à inscrire votre enfant ?
          </h3>
          <p className="text-neutral-700 mb-6 max-w-2xl mx-auto">
            Contactez-nous dès aujourd'hui pour planifier une visite de l'école et découvrir notre environnement éducatif exceptionnel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+212668786368" 
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Appeler maintenant
            </a>
            <a 
              href="mailto:contact@ecoleniddouillet.com" 
              className="inline-flex items-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg border border-primary-600 hover:bg-primary-50 transition-colors"
            >
              <EnvelopeIcon className="h-5 w-5 mr-2" />
              Envoyer un email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
